import { UISpecCategory } from '@common/enums';
import { GemRecord } from '@common/gems/types';
import { SpecLanguage } from '@common/types';
import { WorkflowMode, WorkflowModeType } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Dialog, Input, Select, Stack } from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { validateEntityName } from '@prophecy/utils/validation';
import { capitalize } from 'lodash-es';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useState } from 'react';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { useGemsandLanguages } from './hooks';
import { ComponentListState } from './types';

export function AddGemDialog({
  open,
  onClose,
  updateData,
  isAirflow,
  onAddNewGem
}: {
  open: boolean;
  onClose: () => void;
  updateData: React.Dispatch<React.SetStateAction<ComponentListState>>;
  isAirflow: boolean;
  onAddNewGem: (gem: GemRecord) => void;
}) {
  const globalConf = useGlobalVariables();
  const { gemTypes, languageOpts, language, gemType } = useGemsandLanguages(isAirflow);
  const initialState = {
    name: '',
    category: gemType,
    newGemLanguage: language,
    mode: WorkflowMode.batch as WorkflowModeType | undefined
  };
  const [{ name, category: createGemCategory, newGemLanguage, mode: createGemMode }, setFormData] =
    useState(initialState);

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      category: gemType,
      newGemLanguage: language
    }));
  }, [gemType, language]);

  const handleSubmitCreateGem = useCallback(() => {
    const validationError = validateEntityName(name);
    if (validationError) {
      toast.error({ content: validationError });
      return;
    }

    onAddNewGem({ id: nanoid(), name, category: createGemCategory, language: newGemLanguage, mode: createGemMode });
  }, [name, onAddNewGem, createGemCategory, newGemLanguage, createGemMode]);

  const handleCancelCreateGem = useCallback(() => {
    updateData((state) => ({
      ...state
    }));
    onClose();
  }, [onClose, updateData]);

  const handleNameChange = useCallback((value: string) => {
    setFormData((state) => ({ ...state, name: value }));
  }, []);

  const handlerCategoryChange = useCallback((category: UISpecCategory) => {
    setFormData((state) => ({ ...state, category }));
  }, []);

  const handleLanguageChange = useCallback((language: SpecLanguage) => {
    setFormData((state) => ({ ...state, newGemLanguage: language }));
  }, []);

  const handlelModeChange = useCallback((mode: WorkflowMode) => {
    setFormData((state) => ({ ...state, mode }));
  }, []);

  const dialogTitle = isAirflow ? 'Add Airflow Gem' : 'Add Gem';

  return (
    <Dialog
      size='l'
      title={dialogTitle}
      open={open}
      onOk={handleSubmitCreateGem}
      onClose={handleCancelCreateGem}
      data-test-id='add-gem-dialog'>
      <Stack gap={theme.spaces.x12} padding={`${theme.spaces.x12} 0`}>
        <Stack gap={theme.spaces.x12} direction='horizontal' alignY='center' padding={theme.spaces.x12}>
          <Input
            label='Gem Name'
            onChange={handleNameChange}
            value={name}
            data-test-id='add-gem-dialog-gem-name-input'
          />

          <Select
            options={languageOpts}
            disabled={!globalConf.isPythonLanguageEnabled}
            onChange={handleLanguageChange}
            value={newGemLanguage}
            label={'Language'}
            data-test-id='add-gem-language-select'
          />

          <Select
            dropdownMatchSelectWidth={false}
            options={gemTypes}
            onChange={handlerCategoryChange}
            value={createGemCategory}
            label={'Gem Category'}
            data-test-id='add-gem-category-select'
          />

          {createGemCategory === UISpecCategory.SourceTarget && !isAirflow && (
            <Select
              options={Object.values(WorkflowMode).map((key) => ({ label: capitalize(key), value: key }))}
              onChange={handlelModeChange}
              value={createGemMode}
              label={'Workflow Mode'}
              data-test-id='workflow-mode-select'
            />
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
